/**
 * Pending-analysis queue — analyses attempted without connection relaunch themselves.
 *
 * When analyzeOrder fails because the tablet is offline (photos can't upload, or the
 * item-comparison call itself fails on network), the INTENT is persisted here instead
 * of dying in an error popup. Once the connection is back AND the order's photos have
 * finished uploading, the queue re-fires the item-comparison request silently: the
 * verdict is computed and stored server-side, and the tablet UI updates through the
 * order_analysis realtime event exactly like any other analysis — no popup is forced
 * on an operator who has already moved on to another order.
 *
 * Bounds (agreed design): intents expire after 30 min (a late verdict on a delivered
 * order is noise — the photos still upload regardless, through the photo queue);
 * a manual re-analysis of the same order cancels the queued intent (never two runs);
 * an API failure after reconnection is retried once, then dropped with an
 * `analysis_retry_failed` device event (the order simply stays unanalyzed, as today).
 *
 * Persistence is localStorage (same rationale and deviceId guard as ackQueue.js).
 */
import Logger from '@/logger';
import { restPost } from './restApiFallback.js';
import { reportDeviceEvent } from './deviceEvents.js';
import { reportForensicEvent } from './forensicEvents.js';

const logger = new Logger({ screen: 'analysisQueue' });

const STORAGE_KEY = 'bronze_pending_analyses';
const TTL_MS = 30 * 60 * 1000;
const MAX_ATTEMPTS = 2; // first relaunch + one retry
const TICK_MS = 60000;
const ONLINE_SETTLE_MS = 3000; // 'online' fires before connectivity is usable — don't burn an attempt
// One bag, one analysis. An operator shooting 2-4 frames enqueues once per frame, and
// deduplication by order id only collapses intents that are still PENDING — with a fast
// upload the first frame's analysis has already fired before the second is taken, so the
// burst produces one run per photo. Each run re-notifies the tablet and re-fires the
// order_analysis fan-out. Hold a fresh intent briefly instead: any new photo of the same
// order pushes the deadline out, so the run that does happen is the one that sees them all.
const BURST_SETTLE_MS = 4000;

let deviceId = null;
let restaurantId = null;
let getOrder = null; // (orderId) => order from app state, for a fresh orderAnalysis id
let hasPendingPhotos = null; // async (orderId) => bool — photos still in the upload queue
let onVerdict = null; // (orderId, result) => void — the /item-comparison response, for the result toast
let pending = [];
let tickTimer = null;
let processing = false;
let listenersArmed = false;

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ deviceId, intents: pending }));
  } catch (storageError) {
    logger.console('Could not persist pending analyses', storageError);
  }
}

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.deviceId !== deviceId || !Array.isArray(parsed.intents)) return [];
    return parsed.intents.filter((i) => i && i.order_id);
  } catch (storageError) {
    logger.console('Could not read persisted analyses', storageError);
    return [];
  }
}

function armTick(delayMs = TICK_MS) {
  if (tickTimer) {
    if (delayMs >= TICK_MS) return; // something sooner-or-equal is already armed
    clearTimeout(tickTimer); // a short deadline (upload landed, back online) supersedes
  }
  tickTimer = setTimeout(() => {
    tickTimer = null;
    processQueue();
  }, delayMs);
}

function clearTick() {
  if (tickTimer) {
    clearTimeout(tickTimer);
    tickTimer = null;
  }
}

function onOnline() {
  armTick(ONLINE_SETTLE_MS);
}

function onPhotoUploaded() {
  // A photo just left the local upload queue — fire NOW: if it was the last one of
  // a queued order, hasPendingPhotos turns false and the comparison launches
  // immediately (the backend endpoint does its own active wait for detections).
  if (pending.length > 0) {
    clearTick();
    processQueue();
  }
}

export function initAnalysisQueue(opts) {
  deviceId = opts.deviceId;
  restaurantId = opts.restaurantId;
  getOrder = opts.getOrder;
  hasPendingPhotos = opts.hasPendingPhotos;
  onVerdict = opts.onVerdict || null;
  pending = loadPersisted();
  if (pending.length > 0) {
    // A queue that survived a restart: without this event, an intent that never fires or expires
    // quietly after a reload leaves no trace that it existed.
    reportForensicEvent('analysis_queue_restored', {
      count: pending.length,
      oldest_age_s: Math.round((Date.now() - Math.min(...pending.map((i) => i.queued_at || Date.now()))) / 1000),
    });
  }

  if (!listenersArmed) {
    window.addEventListener('online', onOnline);
    window.addEventListener('photoUploaded', onPhotoUploaded);
    listenersArmed = true;
  }
  if (pending.length > 0) armTick(ONLINE_SETTLE_MS);
}

export function stopAnalysisQueue() {
  clearTick();
  if (listenersArmed) {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('photoUploaded', onPhotoUploaded);
    listenersArmed = false;
  }
  getOrder = null;
  hasPendingPhotos = null;
  onVerdict = null;
}

/**
 * Queue an analysis intent. Replaces any previous intent for the same order.
 * `reason` records WHY the intent was queued (offline | upload_stall | upload_hard_max |
 * background_manual | analysis_timeout | network_error | late-photo) — the event name is
 * historically `analysis_queued_offline`, but offline is only one of the triggers, so the reason
 * disambiguates it in the forensic stream instead of misreading every queue as offline.
 * `late-photo` is the automatic re-analysis: a photo taken after a FAILED verdict, which is the
 * only reason that is not a failure of some kind — it is the normal path, and it is the one to
 * count when measuring how many verdicts the tablet recovers without anyone pressing Analizar.
 */
export function enqueueAnalysis({ orderId, analysisId, reason = 'offline' }) {
  if (!deviceId || !orderId) return;
  pending = pending.filter((i) => i.order_id !== orderId);
  pending.push({
    order_id: orderId,
    analysis_id: analysisId || null,
    queued_at: Date.now(),
    not_before: Date.now() + BURST_SETTLE_MS,
    attempts: 0,
  });
  persist();
  logger.info(`Analysis queued for order ${orderId} (reason=${reason}, relaunches on reconnection)`);
  reportForensicEvent('analysis_queued_offline', {
    order_id: orderId,
    order_analysis_id: analysisId || null,
    reason,
  });
  armTick(BURST_SETTLE_MS);
}

/** A manual analysis supersedes the queued intent — never run the same order twice. */
export function cancelPendingAnalysis(orderId) {
  const before = pending.length;
  pending = pending.filter((i) => i.order_id !== orderId);
  if (pending.length !== before) {
    persist();
    logger.console(`Queued analysis for ${orderId} superseded by a manual run`);
  }
}

export async function processQueue() {
  if (processing || !deviceId || pending.length === 0) return;
  if (!navigator.onLine) return; // next 'online' event re-arms
  processing = true;
  try {
    // `pending` is NOT rebuilt from a snapshot at the end: enqueueAnalysis and cancelPendingAnalysis
    // run while this loop awaits restPost / hasPendingPhotos, and overwriting `pending` with a
    // copy taken before the loop would erase an intent enqueued meanwhile and resurrect one
    // cancelled meanwhile. Instead, record what this pass finished with and subtract it from the
    // CURRENT `pending` at the end.
    const snapshot = [...pending];
    const processed = new Set(); // intent objects this pass completed or dropped
    for (const intent of snapshot) {
      if (!pending.includes(intent)) continue; // cancelled or superseded while earlier ones awaited
      if (Date.now() - intent.queued_at > TTL_MS) {
        let photosPending = null; // null = could not tell
        try {
          photosPending = Boolean(await hasPendingPhotos(intent.order_id));
        } catch (dbError) {
          logger.console('Could not check pending photos for an expired intent', dbError);
        }
        logger.warning(`Dropping expired queued analysis for order ${intent.order_id}`);
        reportForensicEvent('analysis_queue_expired', {
          order_id: intent.order_id,
          order_analysis_id: intent.analysis_id || null,
          photos_pending: photosPending,
          attempts: intent.attempts || 0,
          queued_age_s: Math.round((Date.now() - intent.queued_at) / 1000),
        });
        processed.add(intent);
        continue;
      }

      if (intent.not_before && Date.now() < intent.not_before) {
        // still inside the burst window — a later frame may supersede it
        armTick(Math.max(intent.not_before - Date.now(), 250));
        continue;
      }

      let photosStillUploading = true;
      try {
        photosStillUploading = await hasPendingPhotos(intent.order_id);
      } catch (dbError) {
        logger.console('Could not check pending photos, retrying next tick', dbError);
      }
      if (photosStillUploading) {
        continue; // the analysis needs the photos server-side — wait
      }

      const analysisId = getOrder?.(intent.order_id)?.orderAnalysis || intent.analysis_id;
      if (!analysisId) {
        continue; // id not propagated yet — wait until it is (or TTL)
      }

      reportForensicEvent('analysis_queue_fired', {
        order_id: intent.order_id,
        order_analysis_id: analysisId,
        attempt: (intent.attempts || 0) + 1,
        queued_age_s: Math.round((Date.now() - intent.queued_at) / 1000),
      });
      try {
        const result = await restPost({
          apiName: 'WEBAPP-API',
          path: '/item-comparison',
          options: {
            body: {
              order_id: intent.order_id,
              analysis_id: analysisId,
              restaurant_id: restaurantId,
              device_tag: 'Default',
            },
          },
        });
        // The endpoint reports its own failures inside a 200 envelope (e.g. 408 when
        // the backend timed out waiting for detections) — treat those as retryable,
        // not as success, or a des-synced analysis would silently drop the intent.
        if (result?.statusCode && result.statusCode !== 200) {
          throw new Error(`item-comparison returned statusCode ${result.statusCode}`);
        }
        // Done: the verdict is stored server-side and reaches the tile through the
        // order_analysis realtime event. But that event carries NO missing-item detail
        // and the realtime→processQuery diff can't detect a re-analysis of an
        // already-decided order — so hand the full HTTP result back here for the toast.
        // This fires every time a queued analysis completes (first run and re-runs alike).
        if (onVerdict) {
          try { onVerdict(intent.order_id, result); }
          catch (verdictError) { logger.console('onVerdict handler failed', verdictError); }
        }
        logger.success(`Queued analysis relaunched for order ${intent.order_id}`);
        processed.add(intent);
      } catch (error) {
        intent.attempts = (intent.attempts || 0) + 1;
        if (intent.attempts >= MAX_ATTEMPTS) {
          logger.error(`Queued analysis failed ${intent.attempts}x for ${intent.order_id}, giving up`, { error });
          reportDeviceEvent('analysis_retry_failed', {
            order_id: intent.order_id,
            error: String(error),
          });
          processed.add(intent);
        }
        // else: stays in `pending` for the retry
      }
    }
    pending = pending.filter((i) => !processed.has(i));
    persist();
  } finally {
    processing = false;
    if (pending.length > 0) armTick();
  }
}
